import { createSlice } from '@reduxjs/toolkit'
import { createApiAsyncThunk } from '@/services/apiService'
import { setTimeoutInDev } from '@/utils/environment'
import { addFlashMessage } from '../flash/flashMessagesSlice'
import { sanitizeFormData } from '@/utils/formData'

const URL = 'activities'
const getUrl = (bien_id, id) => {
  // Si on a un bien_id, on est dans le contexte d'un bien
  if(bien_id) {
    if(id) {
      // Route pour une activité spécifique d'un bien
      return `biens/${bien_id}/${URL}/${id}`
    }
    // Route pour toutes les activités d'un bien
    return `biens/${bien_id}/${URL}`
  }
  
  // Si on n'a pas de bien_id, on est hors du contexte d'un bien
  if(id) {
    // Route pour une activité spécifique
    return `${URL}/${id}`
  }
  
  // Route pour toutes les activités
  return URL
}

// États initiaux
const initialState = {
  items: [],
  current: null,
  inEdition: null,
  loadingStatus: {
    fetchList: false,
    fetchItem: false,
    create: false,
    update: false,
    delete: false,
  },
  requestStatus: {
    success: false,
    error: null,
    errors: {}
  },
  filters: {
    page: 1,
    limit: 10
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  }
}

const sanitizeActivityFormData = (data) => {
  const sanitizedData = sanitizeFormData(data)
  sanitizedData.bien_id = sanitizedData.bien?.id
  delete sanitizedData.bien

  return { activity: sanitizedData }
}

// Thunks
export const fetchActivities = createApiAsyncThunk(
  'activities/fetchActivities',
  async (params = {}, { api, dispatch, getState, rejectWithValue }) => {
    try {
      await setTimeoutInDev()
      // Utiliser getUrl pour construire l'URL en fonction du contexte
      let url = params.bien_id ? getUrl(params.bien_id) : getUrl()

      if(params.term) {
        url = `${url}/search`
      }
      
      // Créer une copie des paramètres sans bien_id pour éviter de l'envoyer en double
      const queryParams = { ...params }
      if (queryParams.bien_id) {
        delete queryParams.bien_id
      }
      
      const response = await api
        .url(url)
        .query(queryParams)
        .get()
      return response
    } catch (error) {
      console.error(error)
      return rejectWithValue(error.message || 'Une erreur est survenue lors de la récupération des activités')
    }
  }
)

export const createActivity = createApiAsyncThunk(
  'activities/createActivity',
  async ({data}, { api, dispatch, rejectWithValue }) => {
    try {
      // Une activité peut être créée avec ou sans lien à un bien
      const response = await api
        .url(getUrl())
        .post(sanitizeActivityFormData(data))
      dispatch(addFlashMessage({type: 'success', text: "Activité créée avec succès"}))
      return response
    } catch (error) {
      console.error(error)
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const updateActivity = createApiAsyncThunk(
  'activities/updateActivity',
  async ({ id, data }, { api, dispatch, rejectWithValue }) => {
    try {
      // Une activité peut être mise à jour avec ou sans lien à un bien
      const response = await api
        .url(getUrl(null, id))
        .put(sanitizeActivityFormData(data))
      dispatch(addFlashMessage({type: 'success', text: "Activité mise à jour avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

export const deleteActivity = createApiAsyncThunk(
  'activities/deleteActivity',
  async (activity, { api, dispatch, rejectWithValue }) => {
    try {
      // Une activité peut être supprimée avec ou sans lien à un bien
      const response = await api
        .url(getUrl(null, activity.id))
        .delete()
      dispatch(addFlashMessage({type: 'success', text: "Activité a été supprimée avec succès"}))
      return response
    } catch (error) {
      return rejectWithValue(error.data || { message: error.message, errors: {} })
    }
  }
)

// Valeurs par défaut pour une activité vide
export const emptyActivity = {
  bien_id: null,
  date_activity: '',
  details: '',
  display_to_proprietaire: true,
  display_to_locataire: true,
  display_to_acquereur: true
}

const sanitizesPayload = (action) => Object.fromEntries(
  Object.entries(action.payload).map(([key, value]) =>
    [key, value === null ? (emptyActivity[key] || "") : value]
  )
)

// Slice
const activitiesSlice = createSlice({
  name: 'activities',
  initialState,
  reducers: {
    setEmptyCurrentActivity: (state, action) => {
      const data = {
        ...emptyActivity,
        ...action.payload
      }
      state.current = {
        ...data
      }
      state.requestStatus = initialState.requestStatus
    },
    setCurrentActivity: (state, action) => {
      const sanitizedData = {
        // ...emptyActivity,
        ...sanitizesPayload(action)
      }
      state.current = {
        ...sanitizedData
      }
      state.requestStatus = initialState.requestStatus
    },
    resetCurrentActivity: (state) => {
      state.current = initialState.current
      state.requestStatus = initialState.requestStatus
    },
    setActivityInEdition: (state, action) => {
      state.inEdition = {
        // ...emptyActivity,
        ...action.payload
      }
      state.requestStatus = initialState.requestStatus
    },
    resetActivityInEdition: (state) => {
      state.inEdition = initialState.inEdition
      state.requestStatus = initialState.requestStatus
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetRequestStatus: (state) => {
      state.requestStatus = initialState.requestStatus
    },
  },
  extraReducers: (builder) => {
    // fetchActivities
    builder.addCase(fetchActivities.pending, (state) => {
      state.loadingStatus.fetchList = true
      state.requestStatus.error = null
    })
    builder.addCase(fetchActivities.fulfilled, (state, action) => {
      state.items = action.payload.activities
      state.pagination = {
        totalCount: action.payload.total_count,
        totalPages: action.payload.total_pages,
        currentPage: action.payload.current_page
      }
      state.loadingStatus.fetchList = false
      state.requestStatus.success = true
    })
    builder.addCase(fetchActivities.rejected, (state, action) => {
      state.loadingStatus.fetchList = false
      state.requestStatus.error = action.payload
    })

    // createActivity
    builder.addCase(createActivity.pending, (state) => {
      state.loadingStatus.create = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(createActivity.fulfilled, (state, action) => {
      state.items.unshift(action.payload)
      state.loadingStatus.create = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(createActivity.rejected, (state, action) => {
      state.loadingStatus.create = false
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.error = action.payload
      state.requestStatus.success = false
    })

    // updateActivity
    builder.addCase(updateActivity.pending, (state) => {
      state.loadingStatus.update = true
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(updateActivity.fulfilled, (state, action) => {
      const updated = action.payload
      const index = state.items.findIndex(item => item.id === updated.id)
      if (index !== -1) {
        state.items[index] = updated
      }
      state.loadingStatus.update = false
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = true
    })
    builder.addCase(updateActivity.rejected, (state, action) => {
      state.loadingStatus.update = false
      state.requestStatus.error = action.payload
      state.requestStatus.errors = action.payload.errors ? action.payload.errors[0] : {}
      state.requestStatus.success = false
    })
 
    // Delete Activity
    builder.addCase(deleteActivity.pending, (state) => {
      state.loadingStatus.delete = true
      state.requestStatus.error = null
    })
    builder.addCase(deleteActivity.fulfilled, (state, action) => {
      state.loadingStatus.delete = false
      const index = state.items.findIndex( item => item.id === action.payload.id)
      if (index !== -1) {
        state.items.splice(index, 1)
      }
      state.requestStatus.success = true
    })
    builder.addCase(deleteActivity.rejected, (state, action) => {
      state.loadingStatus.delete = false
      state.requestStatus.error = action.payload
    })
  }
})

export const {
  setEmptyCurrentActivity,
  setCurrentActivity,
  resetCurrentActivity,
  setActivityInEdition,
  resetActivityInEdition,
  setFilters,
  resetRequestStatus,
} = activitiesSlice.actions

export default activitiesSlice.reducer